import { kurveBei, transformAt } from './animation/keyframes.js';
import { gainAt } from './audio/pegel.js';
import { timelineDuration } from './timeline.js';
/**
 * Die Vorschau in Abschnitten (7.71b).
 *
 * Sascha, 10. September: „vielleicht können wir die ‚Film rechnen‘ Funktion
 * dynamisch machen und automatisieren. Muss immer der ganze Film neu?“ —
 * musste er bisher: der gerechnete Film hängt an der Fassungsnummer des
 * Projekts, und die zählt bei *jeder* Änderung hoch. Ein verschobener Text in
 * Minute neun warf zehn Minuten Rechenzeit weg.
 *
 * Also nicht ein Film, sondern viele Abschnitte, jeder mit einem
 * Fingerabdruck über alles, was in ihn hineinspielt: die Clips, die ihn
 * berühren, ihre Lage, ihre Effekte, ihre Keyframes, die Spur, auf der sie
 * liegen, und das Format der Leinwand. Ändert sich etwas, ändert sich genau
 * der Abdruck der Abschnitte, die es betrifft — die anderen bleiben gültig
 * und müssen nicht noch einmal gerechnet werden.
 *
 * Der Abdruck kennt die *Stelle* nicht, an der ein Abschnitt liegt, nur seine
 * Länge und was in ihm passiert. Das ist Absicht: wer vorne etwas einfügt,
 * schiebt alles Spätere nach hinten, ohne dass sich dort ein Bild ändert. Ein
 * Abdruck, der die Stelle mitzählte, würfe an dieser Stelle alles weg.
 */
/** Kürzer als das lohnt kein eigener Abschnitt -- der Aufwand je Lauf frisst es. */
export const KUERZESTER_ABSCHNITT = 2;
/** Länger als das wird geteilt: sonst kostet eine kleine Änderung zu viel. */
export const LAENGSTER_ABSCHNITT = 20;
/** Auf Millisekunden -- Fließkommarauschen darf keinen Abdruck ändern. */
function ms(x) {
    return Math.round(x * 1000) / 1000;
}
/**
 * Ein kurzer, stabiler Abdruck einer Zeichenkette (FNV-1a, 32 Bit, hex).
 *
 * Keine Krypto, sondern ein Schlüssel für einen Zwischenspeicher: er muss
 * überall dasselbe ergeben -- im Browser, im Server, im Arbeitsdienst -- und
 * darf nichts kosten.
 */
export function abdruck(text) {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
}
/** Berührt der Clip das Fenster? Ein Clip, der genau am Ende anstößt, nicht. */
function beruehrt(clip, von, bis) {
    return clip.start < bis - 1e-6 && clip.start + clip.duration > von + 1e-6;
}
/**
 * Ein Clip als Text, wie er in diesem Fenster wirkt: die Zeiten relativ zum
 * Anfang des Fensters, alles andere so, wie es dasteht.
 */
function clipText(clip, von) {
    return JSON.stringify([
        clip.id,
        clip.source,
        ms(clip.start - von),
        ms(clip.duration),
        ms(clip.sourceIn),
        clip.speed,
        clip.gain,
        clip.muted,
        clip.transform,
        clip.keyframes,
        clip.gainKeyframes,
        clip.effects,
        clip.fadeIn,
        clip.fadeOut,
        clip.transitionIn,
        clip.animation,
    ]);
}
function spurText(track) {
    return JSON.stringify([track.id, track.kind, track.muted, track.hidden, track.gain, track.duck, track.duckLevel]);
}
/**
 * Der Abdruck eines Fensters: die Leinwand, die Untertitel und jeder Clip,
 * der es berührt -- in der Reihenfolge der Spuren, damit zweimal dasselbe
 * auch zweimal dasselbe ergibt.
 */
export function fensterAbdruck(timeline, von, bis) {
    const teile = [
        JSON.stringify(timeline.settings),
        JSON.stringify(timeline.subtitles),
        String(timeline.brandKitId),
        `dauer ${ms(bis - von)}`,
    ];
    for (const track of timeline.tracks) {
        const dabei = track.clips.filter((c) => beruehrt(c, von, bis));
        if (dabei.length === 0)
            continue;
        teile.push(spurText(track));
        for (const clip of [...dabei].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))) {
            teile.push(clipText(clip, von));
        }
    }
    return abdruck(teile.join('\n'));
}
/**
 * Die Grenzen, an denen geschnitten werden darf.
 *
 * Jeder Anfang und jedes Ende eines Clips ist eine -- dort ändert sich das
 * Bild ohnehin. Zwei Ausnahmen: mitten in einer Blende darf nicht geschnitten
 * werden (sie gehört als Ganzes in einen Abschnitt), und was näher
 * beieinanderliegt als `KUERZESTER_ABSCHNITT`, wird zusammengefasst.
 */
export function grenzen(timeline, kuerzeste = KUERZESTER_ABSCHNITT, laengste = LAENGSTER_ABSCHNITT) {
    const ende = timelineDuration(timeline);
    if (!(ende > 0))
        return [];
    // Verbotene Stellen: mitten in einer Blende und mitten in einer Ein- oder
    // Ausblendung. Beide gehören als Ganzes in *einen* Abschnitt -- ein
    // Abschnitt, der mitten in einer Einblendung anfängt, stünde sonst
    // plötzlich bei voller Deckung da.
    const gesperrt = [];
    const roh = new Set([0, ms(ende)]);
    for (const track of timeline.tracks) {
        for (const clip of track.clips) {
            roh.add(ms(clip.start));
            roh.add(ms(clip.start + clip.duration));
            const d = clip.transitionIn?.duration;
            if (d)
                gesperrt.push([clip.start - d, clip.start + d]);
            if (clip.fadeIn > 0)
                gesperrt.push([clip.start, clip.start + clip.fadeIn]);
            if (clip.fadeOut > 0)
                gesperrt.push([clip.start + clip.duration - clip.fadeOut, clip.start + clip.duration]);
            // Und eine laufende Bewegung: zwischen zwei Keyframes wird weich
            // beschleunigt und wieder abgebremst (`ease`). Diese Kurve lässt sich
            // nicht in zwei Stücke teilen -- zwei Hälften mit je eigenem Anlauf
            // wären eine andere Bewegung. Also bleibt sie in einem Abschnitt.
            if (clip.keyframes.length > 1) {
                const zeiten = clip.keyframes.map((k) => k.at);
                gesperrt.push([clip.start + Math.min(...zeiten), clip.start + Math.max(...zeiten)]);
            }
        }
    }
    // Offene Bereiche: genau *auf* dem Anfang einer Einblendung zu schneiden ist
    // richtig, mittendrin nicht.
    const verboten = (p) => gesperrt.some(([a, b]) => p > a + 1e-6 && p < b - 1e-6);
    const sortiert = [...roh]
        .filter((p) => p >= 0 && p <= ende)
        .sort((a, b) => a - b)
        .filter((p) => p === 0 || p === ms(ende) || !verboten(p));
    // Zu dicht beieinander: die spätere fällt weg. Das Ende bleibt immer.
    const gefiltert = [];
    for (const p of sortiert) {
        if (gefiltert.length === 0 || p - gefiltert[gefiltert.length - 1] >= kuerzeste)
            gefiltert.push(p);
    }
    if (gefiltert[gefiltert.length - 1] !== ms(ende)) {
        // Der letzte Abschnitt darf kurz sein -- ein Rest ist besser als ein
        // Abschnitt, der über das Ende hinausreicht.
        if (gefiltert.length > 1 && ms(ende) - gefiltert[gefiltert.length - 1] < kuerzeste)
            gefiltert.pop();
        gefiltert.push(ms(ende));
    }
    // Und was zu lang geworden ist, wird geteilt: eine Änderung darin soll
    // nicht zwanzig Sekunden Rechenzeit kosten.
    const fertig = [];
    for (let i = 0; i < gefiltert.length; i++) {
        const p = gefiltert[i];
        fertig.push(p);
        const naechste = gefiltert[i + 1];
        if (naechste === undefined)
            continue;
        const spanne = naechste - p;
        if (spanne <= laengste)
            continue;
        const stuecke = Math.ceil(spanne / laengste);
        for (let k = 1; k < stuecke; k++) {
            const stelle = ms(p + (spanne * k) / stuecke);
            // Eine verbotene Stelle wird nicht geteilt: ein längerer Abschnitt ist
            // besser als eine zerschnittene Blende.
            if (!verboten(stelle))
                fertig.push(stelle);
        }
    }
    return fertig;
}
/** Die Abschnitte eines Schnitts, mit ihren Abdrücken. */
export function abschnitte(timeline, kuerzeste = KUERZESTER_ABSCHNITT, laengste = LAENGSTER_ABSCHNITT) {
    const punkte = grenzen(timeline, kuerzeste, laengste);
    const raus = [];
    for (let i = 0; i + 1 < punkte.length; i++) {
        const von = punkte[i];
        const bis = punkte[i + 1];
        raus.push({ von, bis, abdruck: fensterAbdruck(timeline, von, bis) });
    }
    return raus;
}
/**
 * Der Schnitt eines Fensters als eigener Schnitt -- so, wie ihn der
 * Arbeitsdienst rechnen kann: bei null anfangend, nur mit dem, was in diesem
 * Fenster liegt.
 *
 * Was am Rand angeschnitten wird, wird gestutzt: die Quelle rückt nach, und
 * Keyframes bekommen an der Schnittkante einen eigenen Punkt mit genau dem
 * Wert, den die Bewegung dort hatte. Ohne den spränge eine Ebene am Anfang
 * eines Abschnitts an ihren Ausgangsort zurück.
 *
 * Ein- und Ausblendungen bleiben nur, wenn ihre Seite ganz im Fenster liegt.
 * Angeschnitten werden sie nicht: dafür sorgen die Grenzen (siehe `grenzen`).
 */
export function abschnittSchnitt(timeline, von, bis) {
    return {
        ...timeline,
        tracks: timeline.tracks.map((track) => ({
            ...track,
            clips: track.clips.filter((c) => beruehrt(c, von, bis)).map((clip) => stutze(clip, von, bis)),
        })),
    };
}
function stutze(clip, von, bis) {
    const anfang = Math.max(clip.start, von);
    const ende = Math.min(clip.start + clip.duration, bis);
    const vornAb = ms(anfang - clip.start);
    const hintenAb = ms(clip.start + clip.duration - ende);
    const dauer = Math.max(1 / 1000, ms(ende - anfang));
    const ganz = vornAb === 0 && hintenAb === 0;
    return {
        ...clip,
        start: ms(anfang - von),
        duration: dauer,
        // Die Quelle rückt um das nach, was vorne fehlt -- mal Tempo, denn die
        // Zeitleiste zählt nach dem Tempo, die Quelle davor.
        sourceIn: ms(clip.sourceIn + vornAb * clip.speed),
        keyframes: ganz ? clip.keyframes : gestutzteKeyframes(clip, vornAb, dauer),
        gainKeyframes: ganz ? clip.gainKeyframes : gestutzterPegel(clip, vornAb, dauer),
        fadeIn: vornAb === 0 ? clip.fadeIn : 0,
        fadeOut: hintenAb === 0 ? clip.fadeOut : 0,
        // Eine Blende gehört zu dem Clip davor; fehlt der, gibt es nichts zu
        // blenden. Innerhalb eines Abschnitts liegen beide (siehe `grenzen`).
        transitionIn: vornAb === 0 ? clip.transitionIn : null,
    };
}
/** Höchstens so viele, wie das Modell erlaubt -- Anfang und Ende bleiben. */
function gekappt(liste, hoechstens) {
    if (liste.length <= hoechstens)
        return liste;
    return [liste[0], ...liste.slice(1, hoechstens - 1), liste[liste.length - 1]];
}
function gestutzteKeyframes(clip, vornAb, dauer) {
    if (clip.keyframes.length === 0)
        return [];
    const drin = clip.keyframes
        .filter((k) => k.at > vornAb + 1e-6 && k.at < vornAb + dauer - 1e-6)
        .map((k) => ({ ...k, at: ms(k.at - vornAb) }));
    return gekappt([
        // Die Kurve des angeschnittenen Anfangs ist die, die an dieser Stelle
        // gerade gilt (S30) -- ein Abschnitt soll aussehen wie das Stück, das er
        // ist, und nicht plötzlich weich anfahren, wo der Film durchläuft.
        { at: 0, transform: transformAt(clip, vornAb), kurve: kurveBei(clip, vornAb) },
        ...drin,
        { at: ms(dauer), transform: transformAt(clip, vornAb + dauer), kurve: kurveBei(clip, vornAb + dauer) },
    ], 32);
}
function gestutzterPegel(clip, vornAb, dauer) {
    if (clip.gainKeyframes.length === 0)
        return [];
    const drin = clip.gainKeyframes
        .filter((k) => k.at > vornAb + 1e-6 && k.at < vornAb + dauer - 1e-6)
        .map((k) => ({ ...k, at: ms(k.at - vornAb) }));
    return gekappt([{ at: 0, gain: gainAt(clip, vornAb) }, ...drin, { at: ms(dauer), gain: gainAt(clip, vornAb + dauer) }], 64);
}
/**
 * Der gerechnete Film zu diesem Abschnitt -- gefunden über den Abdruck, nicht
 * über die Stelle. Ein Abschnitt, der sich nur verschoben hat, ist derselbe.
 */
export function filmFuerAbschnitt(renders, a) {
    const passend = renders.filter((r) => r.kind === 'preview' && r.status === 'ready' && Boolean(r.url) && !r.cleaned_at && r.abdruck === a.abdruck);
    if (passend.length === 0)
        return null;
    return [...passend].sort((x, y) => y.created_at.localeCompare(x.created_at))[0];
}
/** Was noch fehlt -- in der Reihenfolge, in der es gebraucht wird. */
export function fehlendeAbschnitte(renders, liste) {
    const laeuft = new Set(renders.filter((r) => r.status === 'queued' || r.status === 'running').map((r) => r.abdruck ?? ''));
    return liste.filter((a) => !filmFuerAbschnitt(renders, a) && !laeuft.has(a.abdruck));
}
/**
 * Was sich abspielen lässt.
 *
 * Zu jedem Abschnitt der gerechnete Film, wenn es einen gibt -- Lücken bleiben
 * Lücken, dort spielt die Vorschau wie bisher die Ebenen. Gibt es noch keinen
 * einzigen Abschnitt, aber einen ganzen Film aus dieser Fassung, ist der das
 * eine Stück: ein Projekt, das vor den Abschnitten gerechnet wurde, bleibt
 * abspielbar.
 */
export function filmStuecke(renders, liste, ganzer, dauer) {
    const stuecke = [];
    for (const a of liste) {
        const film = filmFuerAbschnitt(renders, a);
        if (film?.url)
            stuecke.push({ von: a.von, bis: a.bis, url: film.url, id: film.id });
    }
    if (stuecke.length > 0)
        return stuecke;
    if (ganzer?.url && dauer > 0)
        return [{ von: 0, bis: dauer, url: ganzer.url, id: ganzer.id }];
    return [];
}
/** Das Stück, das an dieser Stelle läuft -- oder keins. */
export function stueckBei(stuecke, stelle) {
    return stuecke.findIndex((s) => stelle >= s.von - 1e-6 && stelle < s.bis - 1e-6);
}
/**
 * Die Naht zwischen zwei Abschnitten (7.71e).
 *
 * Der offene Punkt an 7.71b: „der Ton stösst an einer Abschnittsgrenze hart
 * aneinander“. Bei einem Schnitt fällt das nicht auf -- dort wechselt das
 * Bild ohnehin. Bei einem langen Clip, den `grenzen` nach zwanzig Sekunden
 * teilt, läuft der Ton durch, und genau dort knackt es: jedes Stück ist eine
 * eigene Datei, eigens kodiert, und zwei Kodierungen treffen mitten in einer
 * Schwingung aufeinander. Dazu kommt der Vorlauf, den AAC am Dateianfang
 * braucht -- die ersten Millisekunden sind beim Dekodieren nicht der Ton,
 * der gemeint war.
 *
 * Die Naht zu *verschieben* geht nicht (nachgerechnet am 11. September: in
 * einer Spanne über zwanzig Sekunden gibt es bauartbedingt keine Clipkante
 * und keine Lücke, und den Pegel der Tonspur kennt der gemeinsame Teil
 * nicht). Die Naht *weich* zu machen geht sehr wohl -- und nicht im
 * Abspieler, sondern in der Datei: der Abschnitt wird mit zehn Millisekunden
 * Ein- und Ausblendung auf dem Ton gerechnet. Das ist die Länge, mit der
 * jedes Schnittprogramm ein Knacken wegnimmt: lang genug, dass kein Sprung
 * bleibt, kurz genug, dass niemand eine Lücke hört.
 *
 * Im Abspieler wäre dasselbe nicht machbar: die Vorschau zeichnet fünfmal
 * in der Sekunde neu (7.57), eine Rampe über zehn Millisekunden träfe sie
 * also gar nicht -- und eine Rampe in Stufen ist wieder ein Sprung.
 *
 * Der Abdruck bleibt davon unberührt: die Blende steht im Rechenweg, nicht
 * im Schnitt. Abschnitte, die vor dieser Fassung gerechnet wurden, bleiben
 * gültig und bekommen die Naht bei ihrer nächsten Runde.
 */
export const NAHT = 0.01;
/**
 * Braucht dieser Abschnitt überhaupt einen Film? (7.71d)
 *
 * Sascha, 10. September: „verbessern tut es auch nicht viel“ — auf dem
 * Schreibtisch stimmt das, und auch auf dem Telefon gilt es überall dort, wo
 * ohnehin nur *ein* Video läuft. Der Film hilft genau dann, wenn das Gerät
 * mehrere Videos gleichzeitig dekodieren müsste: zwei Ebenen übereinander,
 * oder eine Blende, bei der zwei Clips ineinander laufen.
 *
 * Alles andere vorzurechnen kostet Rechenzeit und bringt nichts — und ein
 * Fahrplan aus zwanzig roten Abschnitten, von denen neunzehn nichts nützen,
 * ist eine Leiste, die lügt.
 *
 * `istVideo` sagt, ob hinter einer Kennung wirklich ein Video steckt: ein
 * Standbild, eine Farbfläche oder ein Text kostet keinen Dekoder.
 */
export function brauchtFilm(timeline, von, bis, istVideo) {
    const stuecke = [];
    for (const track of timeline.tracks) {
        if (track.kind !== 'video' || track.hidden)
            continue;
        for (const clip of track.clips) {
            if (!beruehrt(clip, von, bis))
                continue;
            // Eine Blende lässt zwei Clips ineinanderlaufen -- zwei Dekoder, auch
            // wenn sie auf derselben Spur liegen.
            if (clip.transitionIn && clip.start > von + 1e-6)
                return true;
            if (clip.source.kind !== 'asset' || !istVideo(clip.source.assetId))
                continue;
            stuecke.push([Math.max(clip.start, von), Math.min(clip.start + clip.duration, bis)]);
        }
    }
    for (let i = 0; i < stuecke.length; i++) {
        for (let k = i + 1; k < stuecke.length; k++) {
            const [a1, a2] = stuecke[i];
            const [b1, b2] = stuecke[k];
            if (a1 < b2 - 1e-6 && b1 < a2 - 1e-6)
                return true;
        }
    }
    return false;
}
/** Die Abschnitte, für die sich das Rechnen lohnt -- in ihrer Reihenfolge. */
export function lohnendeAbschnitte(timeline, liste, istVideo) {
    return liste.filter((a) => brauchtFilm(timeline, a.von, a.bis, istVideo));
}
//# sourceMappingURL=abschnitte.js.map